import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "../store";
import { AppType } from "types/appType";
import { ErrorType } from "types/errorType";
import { Client } from "types/userType";
import { ContactType } from "types/contactType";
import { ExtractErrorInfo } from "components/shared/ExtractErrorInfo";
import { API_ENDPOINTS } from "Config/apiConfig";
import { api } from "Config/Api";
import extractIdFromLocation from "utils/extractIdFromLocation";

export interface AppTypeWithDriverType extends AppType {
  loading: boolean;
  error: ErrorType | null;
  contact: ContactType;
}

const initialState: AppTypeWithDriverType = {
  error: null,
  loading: false,
  contact: {
    name: "",
    client: {} as Client,
    emailAddress: "",
    smsNumber: "",
    telephoneNumber: "",
    address: "",
  },
};

export const createContact = createAsyncThunk<ContactType, { incidentId: string; contact: ContactType }>(
  "contacts/createContact",
  async ({ incidentId, contact }, { rejectWithValue }) => {
    try {
      const response = await api.post<void>(`${API_ENDPOINTS.contacts(incidentId)}`, contact);

      const locationHeader = response.headers["location"] || response.headers["Location"];
      if (!locationHeader) throw new Error("Missing Location header from createContact response");

      return {
        ...contact,
        id: extractIdFromLocation(locationHeader),
      } as ContactType;
    } catch (error: any) {
      return rejectWithValue(ExtractErrorInfo(error));
    }
  }
);

export const updateContact = createAsyncThunk<ContactType, { incidentId: string; contact: ContactType }>(
  "contacts/updateContact",
  async ({ incidentId, contact }, { rejectWithValue }) => {
    try {
      const response = await api.patch<ContactType>(`${API_ENDPOINTS.contacts(incidentId)}/${contact.id}`, contact);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(ExtractErrorInfo(error));
    }
  }
);

export const deleteContact = createAsyncThunk<string, { incidentId: string; contactId: string }>(
  "contacts/deleteContact",
  async ({ incidentId, contactId }, { rejectWithValue }) => {
    try {
      await api.delete(`${API_ENDPOINTS.contacts(incidentId)}/${contactId}`);
      return contactId;
    } catch (error: any) {
      return rejectWithValue(ExtractErrorInfo(error));
    }
  }
);

const contactSlice = createSlice({
  name: "contact",
  initialState,
  reducers: {
    clearForm: (state) => {
      state.contact = initialState.contact;
      state.error = null;
      state.loading = false;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create Contact
      .addCase(createContact.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createContact.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createContact.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as ErrorType;
      })
      // UPDATE contact
      .addCase(updateContact.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateContact.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateContact.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as ErrorType;
      })
      // DELETE contact
      .addCase(deleteContact.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteContact.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(deleteContact.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as ErrorType;
      });
  },
});

export const { clearForm, clearError } = contactSlice.actions;

export const contactRootState = (state: RootState) => state.contact;
export default contactSlice.reducer;
